import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { theme } from '../../theme';

const pulse = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 0.7; transform: scale(1.05); }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(28px); }
  to { opacity: 1; transform: translateY(0); }
`;

const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
`;

export const HeroWrapper = styled.section`
  position: relative;
  min-height: 100vh;
  overflow: hidden;
  padding: 138px 2rem 80px;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(circle at 18% 22%, rgba(${theme.colors.primaryRgb}, 0.17), transparent 32%),
      radial-gradient(circle at 78% 68%, rgba(${theme.colors.secondaryRgb}, 0.14), transparent 28%);
    z-index: 0;
  }

  @media (max-width: 1180px) {
    padding: 116px 2rem 56px;
  }

  @media (max-height: 720px) {
    padding: 108px 2rem 44px;
  }

  @media (max-width: ${theme.breakpoints.md}) {
    min-height: auto;
    padding: 112px 1rem 44px;
  }
`;

export const HeroShell = styled.div`
  position: relative;
  z-index: 1;
  width: min(1240px, 100%);
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(340px, 0.84fr);
  gap: clamp(1.6rem, 3vw, 3rem);
  align-items: center;

  @media (max-width: 1180px) {
    width: min(940px, 100%);
    grid-template-columns: 1fr;
    gap: 2rem;
    align-items: start;
  }

  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

export const GridLines = styled.div`
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(${theme.colors.primaryRgb}, 0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(${theme.colors.primaryRgb}, 0.05) 1px, transparent 1px);
  background-size: 60px 60px;
  z-index: 0;
  mask-image: radial-gradient(circle at center, rgba(0, 0, 0, 0.88), transparent 92%);
`;

export const FloatingOrb = styled.div<{ $size: number; $top: string; $left: string; $delay?: string }>`
  position: absolute;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  background: ${theme.colors.gradient};
  opacity: 0.06;
  filter: blur(${({ $size }) => $size / 2}px);
  top: ${({ $top }) => $top};
  left: ${({ $left }) => $left};
  z-index: 0;
  animation: ${pulse} 6s ease-in-out infinite;
  animation-delay: ${({ $delay }) => $delay ?? '0s'};

  @media (max-width: ${theme.breakpoints.md}) {
    display: none;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const HeroContent = styled.div`
  position: relative;
  max-width: 640px;
  animation: ${slideUp} 0.6s ease both;

  @media (max-width: 1180px) {
    max-width: 100%;
  }

  @media (max-width: ${theme.breakpoints.lg}) {
    max-width: 760px;
  }

  @media (max-width: ${theme.breakpoints.md}) {
    text-align: left;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.45rem 0.95rem;
  border: 1px solid rgba(${theme.colors.primaryRgb}, 0.32);
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${theme.colors.primaryLight};
  background: rgba(${theme.colors.primaryRgb}, 0.08);
  margin-bottom: 1.1rem;

  svg { color: ${theme.colors.primary}; }
`;

export const HeroTitle = styled.h1`
  margin-bottom: 1rem;
  font-family: 'Space Grotesk', 'Manrope', sans-serif;
  font-size: clamp(3rem, 7.2vw, 5.6rem);
  font-weight: 700;
  line-height: 1.02;
  letter-spacing: -0.07em;
  color: ${theme.colors.text};
  text-wrap: balance;

  .gradient {
    color: ${theme.colors.primaryLight};
  }

  .title-tail {
    color: inherit;
  }

  @media (max-width: 1180px) {
    max-width: min(12ch, calc(100vw - 4rem));
    font-size: clamp(2.9rem, 7vw, 4.45rem);
    line-height: 1;
    text-wrap: pretty;
  }

  @media (max-height: 720px) {
    max-width: min(12.5ch, calc(100vw - 4rem));
    font-size: clamp(2.8rem, 6.4vw, 4rem);
    line-height: 0.98;
    margin-bottom: 0.7rem;
  }

  @media (max-width: ${theme.breakpoints.md}) {
    font-size: clamp(2.6rem, 14vw, 4.25rem);
    letter-spacing: -0.08em;
    max-width: min(10.5ch, calc(100vw - 2rem));
    line-height: 0.98;
  }
`;

export const HeroSubtitle = styled.p`
  max-width: 560px;
  margin-bottom: 1.45rem;
  font-size: clamp(1rem, 2.2vw, 1.18rem);
  color: ${theme.colors.textMuted};
  line-height: 1.78;

  @media (max-width: 1180px) {
    max-width: 720px;
  }

  @media (max-height: 720px) {
    margin-bottom: 1rem;
    font-size: 1rem;
    line-height: 1.65;
  }

  @media (max-width: ${theme.breakpoints.md}) {
    margin-bottom: 1.5rem;
    max-width: 100%;
  }
`;

export const HeroButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 1.1rem;

  @media (max-width: ${theme.breakpoints.sm}) {
    display: grid;
    grid-template-columns: 1fr;
  }
`;

export const BtnPrimary = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  min-height: 58px;
  padding: 0.95rem 1.7rem;
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: #fff;
  background: ${theme.colors.gradient};
  border-radius: 18px;
  border: 1px solid rgba(${theme.colors.primaryRgb}, 0.24);
  transition: ${theme.transitions.default};
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, 0.08);
    opacity: 0;
    transition: opacity 0.2s;
  }

  &:hover::after { opacity: 1; }

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.glow};
  }
`;

export const BtnOutline = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  min-height: 58px;
  padding: 0.95rem 1.7rem;
  font-size: 0.95rem;
  font-weight: 700;
  color: ${theme.colors.text};
  background: rgba(8, 12, 18, 0.72);
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 18px;
  transition: ${theme.transitions.default};

  &:hover {
    border-color: rgba(${theme.colors.primaryRgb}, 0.28);
    color: ${theme.colors.primaryLight};
    background: rgba(${theme.colors.primaryRgb}, 0.07);
    transform: translateY(-2px);
  }
`;

export const HeroTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
  margin-bottom: 0.7rem;
`;

export const HeroTag = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 38px;
  padding: 0.5rem 0.8rem;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  background: rgba(9, 14, 20, 0.72);
  color: ${theme.colors.textSecondary};
  font-size: 0.82rem;
  font-weight: 700;
`;

export const HeroNote = styled.p`
  max-width: 510px;
  margin-bottom: 1.2rem;
  color: ${theme.colors.textSecondary};
  font-size: 0.95rem;
  line-height: 1.7;
`;

export const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.7rem;

  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

export const Stat = styled.div`
  padding: 0.9rem 1rem;
  border-radius: 20px;
  background: rgba(8, 12, 18, 0.72);
  border: 1px solid rgba(148, 163, 184, 0.12);

  .value {
    margin-bottom: 0.2rem;
    font-family: 'Space Grotesk', 'Manrope', sans-serif;
    font-size: 2rem;
    font-weight: 700;
    background: ${theme.colors.gradient};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    line-height: 1;
  }

  .label {
    font-size: 0.82rem;
    color: ${theme.colors.textMuted};
  }
`;

export const HeroPanel = styled.aside`
  position: relative;
  padding: 1.15rem;
  border-radius: 28px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  background: linear-gradient(180deg, rgba(15, 23, 34, 0.96), rgba(9, 13, 20, 0.9));
  box-shadow: 0 24px 64px rgba(4, 8, 14, 0.3);
  animation: ${slideUp} 0.7s ease 0.08s both;

  &::before {
    content: '';
    position: absolute;
    inset: 1px;
    border-radius: 28px;
    background: linear-gradient(180deg, rgba(${theme.colors.primaryRgb}, 0.06), rgba(255, 255, 255, 0));
    pointer-events: none;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  @media (max-width: ${theme.breakpoints.md}) {
    padding: 1rem;
    border-radius: 24px;

    &::before {
      border-radius: 22px;
    }
  }
`;

export const PanelIntro = styled.div`
  position: relative;
  z-index: 1;
  margin-bottom: 0.8rem;

  span {
    display: inline-flex;
    margin-bottom: 0.45rem;
    color: ${theme.colors.primaryLight};
    font-size: 0.74rem;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
  }

  h2 {
    margin-bottom: 0.45rem;
    font-size: clamp(1.5rem, 3vw, 2rem);
    line-height: 1;
    color: ${theme.colors.text};
  }

  p {
    color: ${theme.colors.textMuted};
    font-size: 0.95rem;
    line-height: 1.7;
  }
`;

export const ProcessList = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
`;

export const ProcessCard = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.85rem;
  align-items: start;
  padding: 0.95rem 1rem;
  border-radius: 22px;
  border: 1px solid rgba(148, 163, 184, 0.1);
  background: rgba(8, 12, 18, 0.68);
`;

export const ProcessIndex = styled.span`
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  background: rgba(${theme.colors.primaryRgb}, 0.14);
  color: ${theme.colors.primaryLight};
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.08em;
`;

export const ProcessCopy = styled.div`
  strong {
    display: block;
    margin-bottom: 0.28rem;
    font-size: 0.98rem;
    font-weight: 700;
    color: ${theme.colors.text};
  }

  p {
    font-size: 0.88rem;
    line-height: 1.65;
    color: ${theme.colors.textMuted};
  }
`;

export const PanelMetrics = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
  margin-bottom: 0.75rem;

  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

export const MetricCard = styled.div`
  padding: 0.85rem 0.95rem;
  border-radius: 18px;
  border: 1px solid rgba(148, 163, 184, 0.1);
  background: rgba(8, 12, 18, 0.62);

  span {
    display: block;
    margin-bottom: 0.35rem;
    color: ${theme.colors.textMuted};
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  strong {
    color: ${theme.colors.textSecondary};
    font-size: 0.9rem;
    font-weight: 700;
    line-height: 1.35;
  }
`;

export const CodeCard = styled.div`
  position: relative;
  z-index: 1;
  padding: 1rem 1.15rem;
  border-radius: 22px;
  border: 1px solid rgba(148, 163, 184, 0.1);
  background: rgba(7, 10, 16, 0.82);
  font-family: 'Fira Code', monospace;
  font-size: 0.78rem;
  color: ${theme.colors.textMuted};
  line-height: 1.85;
  box-shadow: ${theme.shadows.card};
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    inset: auto auto 0 0;
    width: 100%;
    height: 1px;
    background: linear-gradient(90deg, rgba(${theme.colors.primaryRgb}, 0.5), transparent);
  }

  .keyword { color: ${theme.colors.primaryLight}; }
  .func { color: ${theme.colors.secondaryLight}; }
  .string { color: #d2c2a4; }
  .cursor  {
    display: inline-block;
    width: 2px;
    height: 1em;
    background: ${theme.colors.secondary};
    vertical-align: text-bottom;
    animation: ${blink} 1s step-end infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;
